import { Helmet } from "react-helmet-async";
import AuthLayout from "@/features/auth/auth-layout";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toTitle } from "@/utils/case";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import ForgotPasswordStep1Form, {
  ForgotPasswordStep1FormFields,
} from "./forgot-password-step1-form";
import ForgotPasswordStep2Form, {
  ForgotPasswordStep2FormFields,
} from "./forgot-password-step2-form";
import { useEffect, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";

export type ForgotPasswordFormValues = ForgotPasswordStep1FormFields &
  ForgotPasswordStep2FormFields;

function getIsValidStep(step: 1 | 2, formValues: ForgotPasswordFormValues) {
  if (step === 2) {
    return (
      formValues.mobileNumber &&
      formValues.mobileNumber !== "" &&
      formValues.tempToken &&
      formValues.tempToken !== ""
    );
  }
  // step 1
  return true;
}

export default function ForgotPasswordPage({ step }: { step: 1 | 2 }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tTitle, tSentence } = useI18nHelper();

  const [formValues, setFormValues] = useState<ForgotPasswordFormValues>({
    countryCode: "+82",
    mobileNumber: "",
    mobileVerificationCode: "",
    tempToken: "",
    email: "",
    password: "",
    passwordConfirmation: "",
  });

  const isValidStep = getIsValidStep(step, formValues);

  useEffect(() => {
    if (!isValidStep) {
      navigate("/auth/forgot-password", { replace: true });
    }
  }, [isValidStep, navigate]);

  const tail = (
    <Box fontSize={"12px"}>
      <Link to={"/auth/sign-in"}>
        <Text as="u" fontWeight={"700"}>
          {tSentence("sign_in.do", { ns: "auth" })}
        </Text>
      </Link>
    </Box>
  );

  const handleSubmitStep1 = (
    step1FormValues: ForgotPasswordStep1FormFields
  ) => {
    setFormValues({ ...formValues, ...step1FormValues });
    navigate("/auth/forgot-password/2");
  };

  const form = (() => {
    switch (step) {
      case 2:
        return <ForgotPasswordStep2Form initialValue={formValues} />;
      case 1:
      default:
        return (
          <ForgotPasswordStep1Form
            initialValue={formValues}
            onSubmit={handleSubmitStep1}
          />
        );
    }
  })();

  return (
    <AuthLayout title={toTitle(t("auth:forgot_password.title"))} tail={tail}>
      <Helmet>
        <title>Pathrade | {t("auth:forgot_password.title")}</title>
        <meta name="description" content={t("auth.meta.description")} />
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta property="og:title" content={t("auth:meta.og.title")} />
        <meta property="og:description" content={t("auth:meta.description")} />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      {form}
    </AuthLayout>
  );
}
